import React, { useState, useEffect } from 'react';
import {
  AbsoluteFill,
  useCurrentFrame,
  useVideoConfig,
  spring,
  interpolate,
  Sequence,
} from 'remotion';

const Player = ({ x, y, color, number, label }) => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();
  
  const scale = spring({
    frame,
    fps,
    from: 0,
    to: 1,
    durationInFrames: 10,
  });
  
  return (
    <div
      style={{
        position: 'absolute',
        left: x,
        top: y,
        transform: `translate(-50%, -50%) scale(${scale})`,
      }}
    >
      <div
        style={{
          width: 40,
          height: 40,
          borderRadius: '50%',
          backgroundColor: color,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'white',
          fontWeight: 'bold',
          fontSize: 16,
          border: '3px solid white',
          boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
        }}
      >
        {number}
      </div>
      {label && (
        <div
          style={{
            position: 'absolute',
            top: 45,
            left: '50%',
            transform: 'translateX(-50%)',
            fontSize: 12,
            fontWeight: 'bold',
            color: '#333',
            whiteSpace: 'nowrap',
          }}
        >
          {label}
        </div>
      )}
    </div>
  );
};

const Battery = ({ x, y, rotation = 0 }) => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();
  
  const pulse = Math.sin(frame / 10) * 0.1 + 1;
  
  return (
    <div
      style={{
        position: 'absolute',
        left: x,
        top: y,
        transform: `translate(-50%, -50%) rotate(${rotation}deg) scale(${pulse})`,
      }}
    >
      <svg width="80" height="60" viewBox="0 0 360 360">
        {/* Battery Body - Dark Gray */}
        <rect x="45" y="135" width="270" height="180" fill="#455A64"/>
        {/* Battery Top - Light Gray */}
        <rect x="45" y="105" width="270" height="30" fill="#B0BEC5"/>
        {/* Positive Terminal - Orange/Red */}
        <rect x="240" y="45" width="37.5" height="60" rx="3" fill="#FF6100"/>
        {/* Positive Terminal Band - Dark Red */}
        <rect x="225" y="90" width="67.5" height="15" fill="#D32F2F"/>
        {/* Negative Terminal - Blue */}
        <rect x="82.5" y="45" width="37.5" height="60" rx="3" fill="#2196F3"/>
        {/* Negative Terminal Band - Dark Blue */}
        <rect x="67.5" y="90" width="67.5" height="15" fill="#1565C0"/>
        {/* Plus Symbol - Vertical */}
        <rect x="240" y="187.5" width="22.5" height="67.5" fill="#ECEFF1"/>
        {/* Plus Symbol - Horizontal */}
        <rect x="217.5" y="210" width="67.5" height="22.5" fill="#ECEFF1"/>
        {/* Minus Symbol */}
        <rect x="75" y="210" width="67.5" height="22.5" fill="#ECEFF1"/>
      </svg>
    </div>
  );
};

const Arrow = ({ from, to, color = '#FFD700', delay = 0 }) => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();
  
  const progress = spring({
    frame: frame - delay,
    fps,
    from: 0,
    to: 1,
    durationInFrames: 20,
  });
  
  const dx = to.x - from.x;
  const dy = to.y - from.y;
  const angle = Math.atan2(dy, dx) * 180 / Math.PI;
  const length = Math.sqrt(dx * dx + dy * dy);
  
  return (
    <div
      style={{
        position: 'absolute',
        left: from.x,
        top: from.y,
        transform: `rotate(${angle}deg)`,
        transformOrigin: '0 50%',
      }}
    >
      <svg width={length} height="20" style={{ overflow: 'visible' }}>
        <defs>
          <marker
            id={`arrowhead-${delay}`}
            markerWidth="10"
            markerHeight="10"
            refX="8"
            refY="3"
            orient="auto"
          >
            <polygon points="0 0, 10 3, 0 6" fill={color} />
          </marker>
        </defs>
        <path
          d={`M 0 10 L ${length * progress} 10`}
          stroke={color}
          strokeWidth="3"
          fill="none"
          markerEnd={progress > 0.9 ? `url(#arrowhead-${delay})` : undefined}
        />
      </svg>
    </div>
  );
};

export default function CounterTreyPlay() {
  const frame = useCurrentFrame();
  const { width, height, fps, durationInFrames } = useVideoConfig();
  
  // Field positions
  const fieldCenterX = width / 2;
  const fieldCenterY = height / 2;
  
  // Offensive positions (Blue team)
  const center = { x: fieldCenterX, y: fieldCenterY };
  const leftGuard = { x: fieldCenterX - 40, y: fieldCenterY };
  const rightGuard = { x: fieldCenterX + 40, y: fieldCenterY };
  const leftTackle = { x: fieldCenterX - 80, y: fieldCenterY };
  const rightTackle = { x: fieldCenterX + 80, y: fieldCenterY };
  const tightEnd = { x: fieldCenterX + 120, y: fieldCenterY };
  const quarterback = { x: fieldCenterX, y: fieldCenterY + 50 };
  const runningBack = { x: fieldCenterX, y: fieldCenterY + 100 };
  const fullback = { x: fieldCenterX - 30, y: fieldCenterY + 75 };
  
  // Defensive positions (Red team)
  const defenseY = fieldCenterY - 40;
  
  // Animation progress
  const snapFrame = 60;
  const handoffFrame = 90;
  const pullFrame = 75;
  const leadBlockFrame = 120;
  
  // Calculate positions during play
  const qbHandoffPos = frame > snapFrame ? 
    { 
      x: interpolate(frame, [snapFrame, handoffFrame], [quarterback.x, quarterback.x - 30]),
      y: interpolate(frame, [snapFrame, handoffFrame], [quarterback.y, quarterback.y - 20])
    } : quarterback;
    
  const rbRunPos = frame > handoffFrame ?
    {
      x: interpolate(frame, [handoffFrame, handoffFrame + 60], [runningBack.x, fieldCenterX + 100]),
      y: interpolate(frame, [handoffFrame, handoffFrame + 60], [runningBack.y, fieldCenterY - 80])
    } : runningBack;
    
  const rgPullPos = frame > pullFrame ?
    {
      x: interpolate(frame, [pullFrame, pullFrame + 45], [rightGuard.x, fieldCenterX + 80]),
      y: interpolate(frame, [pullFrame, pullFrame + 45], [rightGuard.y, fieldCenterY - 40])
    } : rightGuard;
    
  const fbLeadPos = frame > snapFrame ?
    {
      x: interpolate(frame, [snapFrame, leadBlockFrame], [fullback.x, fieldCenterX + 60]),
      y: interpolate(frame, [snapFrame, leadBlockFrame], [fullback.y, fieldCenterY - 30])
    } : fullback;
  
  // Battery position follows the ball carrier
  const batteryPos = frame <= handoffFrame ? qbHandoffPos : rbRunPos;
  
  return (
    <AbsoluteFill style={{ backgroundColor: '#90EE90' }}>
      {/* Field lines */}
      <div style={{
        position: 'absolute',
        left: 0,
        right: 0,
        top: fieldCenterY - 5,
        height: 10,
        backgroundColor: 'white',
      }} />
      
      
      {/* Dedicated Text Area - Top section */}
      <div style={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        height: 70,
        backgroundColor: 'rgba(17, 24, 39, 0.9)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        borderBottom: '2px solid #10B981',
        paddingBottom: '10px',
        backdropFilter: 'blur(12px)',
      }}>
        <Sequence from={0} durationInFrames={durationInFrames}>
          <div style={{
            fontSize: 18,
            color: '#fff',
            textAlign: 'center',
            fontWeight: 'bold',
            maxWidth: '95%',
            lineHeight: '1.3',
          }}>
            {frame < snapFrame && "Counter Trey: RB takes handoff going left, then cuts back right"}
            {frame >= snapFrame && frame < handoffFrame && "QB receives the battery snap..."}
            {frame >= handoffFrame && frame < handoffFrame + 30 && "QB hands off the battery to the RB!"}
            {frame >= handoffFrame + 30 && "RB cuts back with pulling guard and fullback leading the charge!"}
          </div>
        </Sequence>
      </div>
      
      {/* Carousel Controls */}
      <div style={{
        position: 'absolute',
        bottom: 20,
        left: '50%',
        transform: 'translateX(-50%)',
        display: 'flex',
        gap: '8px',
        backgroundColor: 'rgba(0,0,0,0.7)',
        padding: '8px 12px',
        borderRadius: '20px',
        alignItems: 'center',
      }}>
        {/* Progress Dots */}
        <div style={{
          display: 'flex',
          gap: '4px',
          alignItems: 'center',
        }}>
          <div style={{
            width: '8px',
            height: '8px',
            borderRadius: '50%',
            backgroundColor: frame < snapFrame ? '#FFD700' : '#666',
            cursor: 'pointer',
          }} />
          <div style={{
            width: '8px',
            height: '8px',
            borderRadius: '50%',
            backgroundColor: frame >= snapFrame && frame < handoffFrame ? '#FFD700' : '#666',
            cursor: 'pointer',
          }} />
          <div style={{
            width: '8px',
            height: '8px',
            borderRadius: '50%',
            backgroundColor: frame >= handoffFrame && frame < handoffFrame + 30 ? '#FFD700' : '#666',
            cursor: 'pointer',
          }} />
          <div style={{
            width: '8px',
            height: '8px',
            borderRadius: '50%',
            backgroundColor: frame >= handoffFrame + 30 ? '#FFD700' : '#666',
            cursor: 'pointer',
          }} />
        </div>
        
        {/* Progress Bar */}
        <div style={{
          width: '100px',
          height: '4px',
          backgroundColor: '#666',
          borderRadius: '2px',
          overflow: 'hidden',
          marginLeft: '8px',
        }}>
          <div style={{
            width: `${(frame / durationInFrames) * 100}%`,
            height: '100%',
            backgroundColor: '#FFD700',
            transition: 'width 0.1s ease',
          }} />
        </div>
        
        {/* Frame Counter */}
        <div style={{
          color: '#fff',
          fontSize: '12px',
          fontWeight: 'bold',
          marginLeft: '8px',
          fontFamily: 'monospace',
        }}>
          {Math.floor(frame / fps)}s
        </div>
      </div>
      
      {/* Offensive Line */}
      <Player x={leftTackle.x} y={leftTackle.y} color="#2196F3" number="LT" />
      <Player x={leftGuard.x} y={leftGuard.y} color="#2196F3" number="LG" />
      <Player x={center.x} y={center.y} color="#2196F3" number="C" />
      <Player x={rgPullPos.x} y={rgPullPos.y} color="#2196F3" number="RG" label={frame > pullFrame ? "PULL!" : ""} />
      <Player x={rightTackle.x} y={rightTackle.y} color="#2196F3" number="RT" />
      <Player x={tightEnd.x} y={tightEnd.y} color="#2196F3" number="TE" />
      
      {/* Backfield */}
      <Player x={qbHandoffPos.x} y={qbHandoffPos.y} color="#1976D2" number="QB" />
      <Player x={rbRunPos.x} y={rbRunPos.y} color="#1976D2" number="RB" label={frame > handoffFrame ? "⚡" : ""} />
      <Player x={fbLeadPos.x} y={fbLeadPos.y} color="#1976D2" number="FB" />
      
      {/* Defense */}
      <Player x={fieldCenterX - 100} y={defenseY} color="#F44336" number="DE" />
      <Player x={fieldCenterX - 40} y={defenseY} color="#F44336" number="DT" />
      <Player x={fieldCenterX + 40} y={defenseY} color="#F44336" number="DT" />
      <Player x={fieldCenterX + 100} y={defenseY} color="#F44336" number="DE" />
      <Player x={fieldCenterX - 60} y={defenseY - 40} color="#D32F2F" number="LB" />
      <Player x={fieldCenterX + 60} y={defenseY - 40} color="#D32F2F" number="LB" />
      
      {/* Run to Failure Arrow - Behind text */}
      {frame >= 0 && frame < 90 && (
        <Sequence from={0} durationInFrames={90}>
          <Arrow 
            from={{x: 140, y: 100}} 
            to={{x: batteryPos.x - 40, y: batteryPos.y - 10}} 
            color="#EF4444" 
            delay={10}
          />
        </Sequence>
      )}
      
      {/* Predictive AI Arrow - Behind text */}
      {frame >= 90 && (
        <Sequence from={90} durationInFrames={90}>
          <Arrow 
            from={{x: width - 140, y: 100}} 
            to={{x: batteryPos.x + 40, y: batteryPos.y - 10}} 
            color="#10B981" 
            delay={100}
          />
        </Sequence>
      )}
      
      {/* Run to Failure Text - Fixed position top left */}
      {frame >= 0 && frame < 90 && (
        <Sequence from={0} durationInFrames={90}>
          <div style={{
            position: 'absolute',
            left: 20,
            top: 80,
            backgroundColor: 'rgba(17, 24, 39, 0.95)',
            color: '#fff',
            padding: '12px 16px',
            borderRadius: '8px',
            fontSize: 18,
            fontWeight: 'bold',
            border: '2px solid #EF4444',
            whiteSpace: 'nowrap',
            boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
            zIndex: 10,
          }}>
            Run to Failure
          </div>
        </Sequence>
      )}
      
      {/* Predictive AI Text - Fixed position top right */}
      {frame >= 90 && (
        <Sequence from={90} durationInFrames={90}>
          <div style={{
            position: 'absolute',
            right: 20,
            top: 80,
            backgroundColor: 'rgba(17, 24, 39, 0.95)',
            color: '#fff',
            padding: '12px 16px',
            borderRadius: '8px',
            fontSize: 18,
            fontWeight: 'bold',
            border: '2px solid #10B981',
            boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
            textAlign: 'center',
            whiteSpace: 'nowrap',
            zIndex: 10,
          }}>
            Predictive AI
          </div>
        </Sequence>
      )}
      
      {/* Battery - On top of everything */}
      <div style={{ zIndex: 20 }}>
        <Battery x={batteryPos.x} y={batteryPos.y - 20} rotation={frame * 2} />
      </div>
      
      {/* Movement arrows */}
      {frame > snapFrame && frame < handoffFrame && (
        <Arrow from={center} to={quarterback} color="#4CAF50" />
      )}
      {frame > handoffFrame && (
        <Arrow from={runningBack} to={{x: fieldCenterX + 100, y: fieldCenterY - 80}} color="#FFD700" delay={handoffFrame} />
      )}
      {frame > pullFrame && (
        <Arrow from={rightGuard} to={{x: fieldCenterX + 80, y: fieldCenterY - 40}} color="#FF9800" delay={pullFrame} />
      )}
      {frame > snapFrame && (
        <Arrow from={fullback} to={{x: fieldCenterX + 60, y: fieldCenterY - 30}} color="#9C27B0" delay={snapFrame} />
      )}
    </AbsoluteFill>
  );
}